import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import fetchData from "../../../api/fetchApi";
import { ArrowRight, Save, Activity, CreditCard, User, Wallet } from "lucide-react";

export default function EditAccountPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [account, setAccount] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    description: "",
    is_active: true,
  });

  useEffect(() => {
    const getAccount = async () => {
      try {
        setLoading(true);
        const response = await fetchData(`finance/accounts/${id}`);
        setAccount(response.data);
        setFormData({
          description: response.data.description || "",
          is_active: response.data.is_active,
        });
      } catch (err) {
        setError(err.message || "فشل في جلب بيانات الحساب");
      } finally {
        setLoading(false);
      }
    };

    getAccount();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await fetchData(`finance/accounts/${id}`, "PUT", formData);
     
    } catch (err) {
      setError(err.message || "فشل في تحديث الحساب");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center min-h-[400px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );

  if (!account) return (
    <div className="p-6 text-center text-red-600">
      {error || "الحساب غير موجود"}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <ArrowRight size={24} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">تعديل الحساب #{account.id}</h1>
            <p className="text-gray-500 text-sm">تعديل بيانات الحساب وحالته</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 text-red-600 p-4 rounded-lg border border-red-100">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Main Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Wallet className="text-blue-500" size={20} />
              معلومات الحساب الأساسية
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <label className="text-xs text-gray-500 mb-1 block">مالك الحساب</label>
                <div className="font-medium flex items-center gap-2">
                  <User size={16} className="text-gray-400" />
                  {account.owner?.name || "N/A"}
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <label className="text-xs text-gray-500 mb-1 block">النوع</label>
                <div className="font-medium flex items-center gap-2">
                  <CreditCard size={16} className="text-gray-400" />
                  {account.type}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <label className="text-xs text-gray-500 mb-1 block">الرصيد الحالي</label>
                <div className="font-bold text-lg text-green-600">
                  {account.balance} {account.currency?.symbol}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <label className="text-xs text-gray-500 mb-1 block">آخر عملية</label>
                <div className="font-medium text-gray-900">
                  {account.last_transaction || "-"}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الوصف
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow h-24"
                  placeholder="أدخل وصفاً للحساب..."
                />
              </div>

              <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}>
                <div className={`w-12 h-6 rounded-full p-1 transition-colors duration-200 ease-in-out ${formData.is_active ? 'bg-green-500' : 'bg-gray-300'}`}>
                  <div className={`bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform duration-200 ease-in-out ${formData.is_active ? 'translate-x-0' : '-translate-x-6'}`} />
                </div>
                <div className="flex-1">
                  <span className="block text-sm font-medium text-gray-900">حالة الحساب</span>
                  <span className="block text-xs text-gray-500">
                    {formData.is_active ? 'الحساب نشط ويمكن استخدامه' : 'الحساب معطل ومقيد الاستخدام'}
                  </span>
                </div>
                <Activity size={20} className={formData.is_active ? 'text-green-500' : 'text-gray-400'} />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <Save size={18} />
              {saving ? "جاري الحفظ..." : "حفظ التغييرات"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
